<!-- eslint-disable @intlify/vue-i18n/no-dynamic-keys -->
<script setup>
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { messageStamp } from 'shared/helpers/timeHelper';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'next/avatar/Avatar.vue';
import AuditTrailChanges from 'next/wgpt/AuditTrail/AuditTrailChanges.vue';

const props = defineProps({
  record: { type: Object, required: true },
});

const { t } = useI18n();

const dateText = computed(() => {
  const date = new Date(props.record.date);
  return messageStamp(date.getTime(), 'LLL d, y, h:mm aa');
});

const modelText = computed(() => {
  const { type, id } = props.record.model;
  const o = useSnakeCase({ [type]: true });
  const snakeCaseModel = Object.keys(o)[0].toUpperCase();
  const name = t(`WGPT_AUDIT_TRAIL.MODELS.${snakeCaseModel}`, type);
  return `${name} #${id}`;
});

const userText = computed(() => {
  const { user } = props.record;
  if (user) return `${user.name}`;
  return t('WGPT_AUDIT_TRAIL.COMMON.SYSTEM_USER');
});

const actionText = computed(() => {
  const { action, changes = {} } = props.record;
  return t(`WGPT_AUDIT_TRAIL.COMMON.ACTION_MESSAGE.${action.toUpperCase()}`, {
    user: userText.value,
    model: modelText.value,
    count: Object.keys(changes).length,
  });
});

const ipText = computed(() => {
  return `· ${props.record.ip || '-'}`;
});
</script>

<template>
  <div class="flex gap-3 group">
    <Avatar :src="record.user?.thumbnail || ''" :name="userText" />

    <div class="flex-1">
      <div class="row-text text-sm font-medium">
        {{ actionText }}
      </div>

      <div class="text-n-slate-9 row-text text-xs font-medium">
        <span>{{ dateText }}</span>
        <span class="group-hover:visible invisible">{{ ipText }}</span>
      </div>

      <div
        class="transition-all duration-500 ease-in-out grid overflow-hidden grid-rows-[1fr] opacity-100"
      >
        <div class="overflow-hidden">
          <div class="flex flex-col py-4 gap-4">
            <AuditTrailChanges
              :model="modelText"
              :action="record.action"
              :changes="record.changes"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.collapsed .row-text {
  @apply line-clamp-1 break-all;
}
</style>
